import api from './api';

/**
 * COSTING.1a-web (E2) — client for an item's cost layers + stat aggregates.
 * GET /admin/venue/:venueId/inventory/item/:itemId/cost-layers (ADMIN_ONLY,
 * interim gating; permissions-array mechanism lands in COSTING.1d).
 *
 * All money is in minor units — convert at display with fromMinorUnits.
 */

export interface CostLayerRow {
  layerId: string;
  sourceType: string;
  costBasis: string;
  lotId: string | null;
  lotNumber: string | null;
  unitCost: number | null;   // minor units
  originalQty: number;
  remainingQty: number;
  value: number | null;      // minor units
  receivedDate: string;      // ISO
  supplierName: string | null;
}

export interface ItemCostLayerView {
  active: boolean;
  headUnitCost: number | null;      // minor units
  fallbackUnitCost: number | null;  // minor units
  lastReceipt: { unitCost: number | null; receivedDate: string; supplierName: string | null } | null;
  valueOnHand: number;              // minor units
  totalRemainingUnits: number;
  uncostedUnits: number;
  layers: CostLayerRow[];
}

export interface CostingStatus {
  active: boolean;
  activatedAt: string | null;  // ISO when active; null while dormant
}

const itemCostLayerService = {
  getItemCostLayers: async (venueId: string, itemId: string): Promise<ItemCostLayerView> => {
    const response = await api.get(`/admin/venue/${venueId}/inventory/item/${itemId}/cost-layers`);
    return response.data;
  },
  /** COSTING.1a-web (E6): venue costing status for the Inventory settings page. */
  getCostingStatus: async (venueId: string): Promise<CostingStatus> => {
    const response = await api.get(`/admin/venue/${venueId}/inventory/costing-status`);
    return response.data;
  },
  /** COSTING.1c (E6): ADMIN activation of cost tracking. Idempotent server-side. */
  activateCosting: async (venueId: string): Promise<ActivateCostingResult> => {
    const response = await api.post(`/admin/venue/${venueId}/inventory/costing/activate`);
    return response.data;
  },
  /** COSTING.1d (E2): ADMIN correct a cost layer's unitCost (minor units), notes required. */
  correctCostLayer: async (venueId: string, layerId: string, unitCost: number, notes: string): Promise<CorrectLayerResult> => {
    const response = await api.post(`/admin/venue/${venueId}/inventory/cost-layer/${layerId}/correct`, { unitCost, notes });
    return response.data;
  },
};

export interface ActivateCostingResult {
  venueId: string;
  alreadyActive: boolean;
  balancesScanned: number;
  layersCreated: number;
}

export interface CorrectLayerResult {
  layerId: string;
  mutated: boolean;
  previousUnitCost: number | null;
  newUnitCost: number;
  valueDelta: number;
  movementId: string;
}

export default itemCostLayerService;
